import { isNil, get } from 'lodash';

/**
 * Gets the configuration id.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The id for the configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getId = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, '_id', '');
};

/**
 * Gets configuration created time.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The configuration last update time or an empty string if updatedAt is undefined.
 * @throws Error if configuration object is undefined.
 */
export const getCreated = configurationObject => {
  if (isNil(configurationObject)) {
    throw new Error('configuration not defined');
  }
  return get(configurationObject, 'createdAt', '');
};

/**
 * Gets configuration last update time.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The configuration last update time or an empty string if updatedAt is undefined.
 * @throws Error if configuration object is undefined.
 */
export const getLastUpdate = configurationObject => {
  if (isNil(configurationObject)) {
    throw new Error('configuration not defined');
  }
  return get(configurationObject, 'updatedAt', '');
};

/**
 * Checks if the configuration is deleted.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {boolean} True if the configuration is in trash, false otherwise.
 * @throws {Error} If configuration is null or undefined.
 */
export const isDeleted = configurationObject => {
  if (isNil(configurationObject)) throw new Error('connection not defined');
  return get(configurationObject, 'deleted', '') !== 'no';
};

/**
 * Get configuration name.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The name of the configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getName = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, 'name', '');
};

/**
 * Get configuration video id asociated.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getVideoId = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, 'videoId', '');
};

/**
 * Get configuration template id asociated.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getTemplateId = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, 'templateId', '');
};

/**
 * Get configuration connector id asociated.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getConnectorId = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, 'connectorId', '');
};

/**
 * Get configuration source asociated.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getSource = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, 'source', '');
};

/**
 * Get configuration show data is actived.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const isOnlyGeometry = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, 'isOnlyGeometry', false);
};

/**
 * Get configuration fields connections.
 *
 * @param {object} configurationObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getConnectionsFields = configurationObject => {
  if (isNil(configurationObject)) throw new Error('configuration not defined');
  return get(configurationObject, 'connectionsFields', []);
};

/**
 * Get of connections fields name of column table.
 *
 * @param {object} connectionFieldObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getNameField = connectionFieldObject => {
  if (isNil(connectionFieldObject)) throw new Error('configuration not defined');
  return get(connectionFieldObject, 'nameField', '');
};
/**
 * Get of connections fields value of template to save in table.
 *
 * @param {object} connectionFieldObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getValue = connectionFieldObject => {
  if (isNil(connectionFieldObject)) throw new Error('configuration not defined');
  return get(connectionFieldObject, 'value', '');
};
/**
 * Get of connections fields is elements array sort.
 *
 * @param {object} connectionFieldObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const isOrderElements = connectionFieldObject => {
  if (isNil(connectionFieldObject)) throw new Error('configuration not defined');
  return get(connectionFieldObject, 'isOrderElements', true);
};
/**
 * Get of connections fields element index asociated.
 *
 * @param {object} connectionFieldObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getElementIndex = connectionFieldObject => {
  if (isNil(connectionFieldObject)) throw new Error('configuration not defined');
  return get(connectionFieldObject, 'elementIndex', -1);
};
/**
 * Get of connections fields index element asociated to other element (date/hour).
 *
 * @param {object} connectionFieldObject - The configuration object.
 * @returns {string} The id of the video asociated at configuration or empty string if not found.
 * @throws {Error} If configuration is null or undefined.
 */
export const getElementAsociatedIndex = connectionFieldObject => {
  if (isNil(connectionFieldObject)) throw new Error('configuration not defined');
  return get(connectionFieldObject, 'elementAsociatedIndex', -1);
};
